import { Component, EventEmitter, Input, Output, TemplateRef } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Animacao } from '../../../core/tools/animacao';
import { Cliente } from '../../../core/models/clientes/cliente';
import { Pedido } from '../../../core/models/pedidos/pedido';
import { ProdutoPromocional } from '../../../core/models/produtos/produto-promocional';

/**
 * Bloco "Cliente (Filtrar)" do Step 0 (Cabeçalho) do wizard de pedidos:
 * formulário de filtro de cliente + listagem virtualizada de resultados
 * (pedidos-cadastro.component.html:649-748).
 *
 * Gate real de visibilidade do bloco inteiro (html:650): só é exibido para
 * pedidos genuinamente novos — `(pedido == null && produtosAdicionados.length
 * == 0 && clienteSelecionadoExterno == '') || duplicandoPedido`. Reproduzido
 * aqui como *ngIf calculado a partir de @Input()s, mesmo precedente da Task 1
 * da Phase 1 (`pedido-itens-catalogo.component`, gate `produtoSelecionado ==
 * null && !isEditMode`).
 *
 * Botão "Locais de Entrega" (html:706-710): único gatilho desse botão em todo
 * o Step 0 — verificado via grep em todo o arquivo (`grep -n "Locais de
 * Entrega\|modalLocaisEntrega\|locaisEntregaModal"`), não há uma segunda
 * ocorrência para o caso de edição de pedido existente. Portanto está 100%
 * coberto por este componente; a Task 2 (`pedido-cliente-selecionado`) não
 * precisa (nem deve) reimplementá-lo.
 *
 * O `<ng-template #locaisEntregaModal>` referenciado pelo botão permanece no
 * pai (html:398) — recebido aqui via @Input() `modalLocaisEntrega`, mesmo
 * padrão de `[modalLotes]`/`[modalCampanha]` da Phase 1 (Task 5).
 *
 * Componente puramente apresentacional: toda ação de negócio (submeter
 * filtro, alterar filtro, selecionar cliente, abrir modal) é reemitida via
 * @Output() para o pai reagir, que continua com os métodos reais
 * (`filtrarCliente`, `alterarFiltroCliente`, `selecionarClienteFiltrado`,
 * `modalLocaisEntrega`) inalterados (wiring feito na Task 4).
 */
@Component({
  selector: 'app-pedido-cliente-busca',
  standalone: false,
  templateUrl: './pedido-cliente-busca.component.html',
  styleUrls: ['./pedido-cliente-busca.component.scss'],
  animations: [Animacao]
})
export class PedidoClienteBuscaComponent {
  /* Gate do bloco inteiro (html:650). */
  @Input() pedido: Pedido = null;
  @Input() produtosAdicionados: ProdutoPromocional[] = [];
  @Input() clienteSelecionadoExterno = '';
  @Input() duplicandoPedido = false;

  /* Formulário de filtro — real: ts:611-617 (`filtro`, `codigo`, `cnpj_cpf`,
   * `nome_fantasia`, `razao_social`), getter `fc` em ts:727 (não atravessa a
   * fronteira do componente — o template usa `formFiltroCliente.controls[...]`
   * diretamente, conforme brief). */
  @Input() formFiltroCliente: UntypedFormGroup = null;
  @Input() filtroClientes: any[] = [];
  @Input() enviadoCliente = false;

  /* Resultados */
  @Input() filtrandoCliente = false;
  @Input() clientes: Cliente[] = [];
  @Input() clienteSelecionado: Cliente = null;

  /* Virtual scroll (html:719: `[itemSize]="10"` fixo no original). */
  @Input() itemSize = 10;

  /** Abaixo de 992px cada linha vira um card empilhado de ~150px (5 pares
   *  rótulo/valor), bem maior que a linha compacta do desktop — o
   *  cdk-virtual-scroll-viewport precisa de um itemSize próximo da altura
   *  real do item para calcular a área visível corretamente (ajuste de
   *  performance/scroll do virtual scroll, não altera nenhuma regra de
   *  negócio). Reavaliado a cada ciclo de detecção de mudanças, incluindo em
   *  resize (zone.js já dispara CD em `window resize`). */
  get itemSizeEfetivo(): number {
    return typeof window !== 'undefined' && window.innerWidth < 992 ? 150 : this.itemSize;
  }

  /** TemplateRef do modal de Locais de Entrega (`#locaisEntregaModal` no pai, html:398). */
  @Input() modalLocaisEntrega: TemplateRef<any> = null;

  @Output() filtroSubmetido = new EventEmitter<void>();
  /** Espelha `alterarFiltroCliente(filtro_selecionado: number)` — dual-purpose:
   *  valor do `<select>` no `(change)` ou `null` no Cancelar (ts:7334). */
  @Output() filtroAlterado = new EventEmitter<any>();
  /** Espelha a chamada real `selecionarClienteFiltrado(cliente.codigo_no_erp_do_cliente, false)`
   *  (html:732) — o call site real NÃO passa o 3º parâmetro (`recebendo_dados`)
   *  do método (ts:7607), apesar de o método não ter valor default para ele;
   *  reproduzido tal como está no código-fonte (ver relatório da Task 1). */
  @Output() clienteEscolhido = new EventEmitter<{ codigo: string; existente: boolean }>();
  @Output() abrirLocaisEntrega = new EventEmitter<TemplateRef<any>>();

  /** TrackBy estável por código do cliente, mesmo precedente de
   *  `trackByProdutoId` da Phase 1 (Task 1 do catálogo de itens) — não existe
   *  no `*cdkVirtualFor` original (que não tinha trackBy), adicionado aqui
   *  apenas como otimização de diffing do DOM, sem alterar comportamento. */
  trackByCodigo(index: number, cliente: any): any {
    return cliente != null ? cliente.codigo_no_erp_do_cliente : index;
  }
}
